// Cálculos del módulo "Beneficiarios sin datos" (tabla promocion reciclada).
//
// Las mismas dos reglas las valida el backend (PromocionController). Aquí se
// calculan en vivo para avisar mientras se escribe, antes de enviar:
//   1. masculino + femenino = total
//   2. la suma de los grupos de edad = total
// Si se registran 500 personas y los grupos de edad suman 400, hay que avisar
// dónde están las otras 100.

export const aEntero = (valor) => {
  const n = parseInt(valor, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export const sumarDistribucion = (distribucion) =>
  (Array.isArray(distribucion) ? distribucion : [])
    .reduce((acc, fila) => acc + aEntero(fila?.cantidad), 0);

/**
 * Compara una suma contra el total.
 * @returns {{ ok: boolean, suma: number, total: number, diferencia: number, mensaje: string }}
 */
export const cuadre = (suma, total, destino) => {
  const diferencia = total - suma;

  // Todavía no hay total: no es un error, es una indicación. Se marca como
  // `pendiente` para que el formulario lo muestre en gris y no en rojo, que
  // hacía parecer que la sección estaba bloqueada.
  if (total <= 0) {
    return {
      ok: false, pendiente: true, suma, total, diferencia,
      mensaje: `Escriba el total de beneficiarios y repártalo ${destino}.`,
    };
  }
  if (diferencia === 0) {
    return { ok: true, suma, total, diferencia, mensaje: `Cuadra: ${suma} de ${total}.` };
  }
  if (diferencia > 0) {
    return {
      ok: false, suma, total, diferencia,
      mensaje: `Suman ${suma} de ${total}. Faltan ${diferencia} personas por asignar ${destino}.`,
    };
  }
  return {
    ok: false, suma, total, diferencia,
    mensaje: `Suman ${suma} y el total es ${total}. Sobran ${-diferencia} personas.`,
  };
};

export const cuadreGenero = (form) =>
  cuadre(aEntero(form?.cantidad_masculino) + aEntero(form?.cantidad_femenino), aEntero(form?.cantidad_total), 'a un género');

export const cuadreEdades = (form, distribucion) =>
  cuadre(sumarDistribucion(distribucion), aEntero(form?.cantidad_total), 'a un grupo de edad');

/**
 * Una fila por grupo de edad activo, con la cantidad ya guardada si existe.
 * Así el formulario siempre muestra todos los grupos, incluidos los que están en 0.
 */
export const distribucionInicial = (grupos, existente = []) => {
  const guardado = new Map(
    (Array.isArray(existente) ? existente : []).map((f) => [Number(f.grupo_etario_id), aEntero(f.cantidad)])
  );

  return (Array.isArray(grupos) ? grupos : []).map((g) => ({
    grupo_etario_id: g.id,
    descripcion: g.descripcion,
    cantidad: guardado.has(Number(g.id)) ? String(guardado.get(Number(g.id))) : '',
  }));
};

/** Lista de problemas que impiden guardar. Vacía = se puede enviar. */
export const validarFormulario = (form, distribucion) => {
  const errores = [];
  const obligatorios = [
    ['cohorte', 'la cohorte'],
    ['periodo_id', 'el período'],
    ['centro_id', 'el centro'],
    ['fecha_promocion', 'la fecha de registro'],
    ['procedencia_id', 'la procedencia'],
    ['estado_id', 'el estado'],
  ];

  obligatorios.forEach(([campo, nombre]) => {
    if (String(form?.[campo] ?? '').trim() === '') errores.push(`Falta ${nombre}.`);
  });

  if (aEntero(form?.cantidad_total) < 1) {
    errores.push('El total de beneficiarios debe ser al menos 1.');
  } else {
    const genero = cuadreGenero(form);
    if (!genero.ok) errores.push(`Género: ${genero.mensaje}`);

    const edades = cuadreEdades(form, distribucion);
    if (!edades.ok) errores.push(`Grupos de edad: ${edades.mensaje}`);
  }

  if (form?.es_patrocinado && !form?.patrocinante_id) {
    errores.push('Si el caso es patrocinado, indique el patrocinante.');
  }

  if (form?.realiza_aporte) {
    const monto = parseFloat(form?.monto);
    if (!Number.isFinite(monto) || monto <= 0) errores.push('Si el caso realiza aporte, indique el monto.');
    if (!form?.tipo_moneda) errores.push('Indique la moneda del aporte.');
  }

  return errores;
};

/** Equivalente en dólares, con la misma regla que el backend. */
export const montoEnUsd = (monto, moneda, tasa) => {
  const m = parseFloat(monto);
  const t = parseFloat(tasa);
  if (!Number.isFinite(m)) return null;
  if (moneda === '$') return m;
  if (moneda === 'bsF' && Number.isFinite(t) && t > 0) return m / t;
  return null;
};

export const formatoNumero = (n, decimales = 0) =>
  Number(n || 0).toLocaleString('es-VE', {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  });

/** Construye el cuerpo que espera POST/PUT /promocion. */
export const construirPayload = (form, distribucion) => ({
  // El formulario ya no pide el nombre del caso (26/09/2026); si un registro
  // viejo lo tenía, se conserva al editar.
  nombre: form.nombre?.trim() || null,
  fecha_promocion: form.fecha_promocion,
  comentarios: form.comentarios || null,

  cantidad_total: aEntero(form.cantidad_total),
  cantidad_masculino: aEntero(form.cantidad_masculino),
  cantidad_femenino: aEntero(form.cantidad_femenino),
  distribucion_edades: distribucion.map((f) => ({
    grupo_etario_id: f.grupo_etario_id,
    cantidad: aEntero(f.cantidad),
  })),

  procedencia_id: form.procedencia_id || null,
  estado_id: form.estado_id || null,
  municipio: form.municipio || null,
  grupo_prioritario_id: form.grupo_prioritario_id || null,

  centro_id: form.centro_id || null,
  cohorte: form.cohorte || null,
  periodo_id: form.periodo_id || null,
  curso_id: form.curso_id || null,
  grupo: form.grupo || null,

  // Un solo patrocinante: las posiciones 2 y 3 ya no se envían.
  es_patrocinado: Boolean(form.es_patrocinado),
  patrocinante_id: form.es_patrocinado ? form.patrocinante_id || null : null,

  // Sin aporte, el backend deja todos los campos de pago vacíos.
  realiza_aporte: Boolean(form.realiza_aporte),
  monto: form.realiza_aporte ? form.monto : null,
  tipo_moneda: form.realiza_aporte ? form.tipo_moneda : null,
  tasa_bcv_id: form.realiza_aporte ? form.tasa_bcv_id || null : null,
  forma_pago_id: form.realiza_aporte ? form.forma_pago_id || null : null,
  fecha_pago: form.realiza_aporte ? form.fecha_pago || null : null,
  numero_comprobante: form.realiza_aporte ? form.numero_comprobante || null : null,
});

/** Estado inicial del formulario, a partir de un registro guardado (edición) o vacío (alta). */
export const formularioInicial = (registro) => ({
  nombre: registro?.nombre ?? '',
  fecha_promocion: (registro?.fecha_promocion ?? '').toString().slice(0, 10),
  comentarios: registro?.comentarios ?? '',

  cantidad_total: registro?.cantidad_total != null ? String(registro.cantidad_total) : '',
  cantidad_masculino: registro?.cantidad_masculino != null ? String(registro.cantidad_masculino) : '',
  cantidad_femenino: registro?.cantidad_femenino != null ? String(registro.cantidad_femenino) : '',

  procedencia_id: registro?.procedencia_id ?? '',
  estado_id: registro?.estado_id ?? '',
  municipio: registro?.municipio ?? '',
  grupo_prioritario_id: registro?.grupo_prioritario_id ?? '',

  centro_id: registro?.centro_id ?? '',
  cohorte: registro?.cohorte ?? '',
  periodo_id: registro?.periodo_id ?? '',
  curso_id: registro?.curso_id ?? '',
  grupo: registro?.grupo ?? '',

  es_patrocinado: Boolean(registro?.es_patrocinado),
  patrocinante_id: registro?.patrocinante_id ?? '',

  // Los registros anteriores al ajuste no traen `realiza_aporte`: si tienen
  // monto cargado, realizan aporte.
  realiza_aporte: registro?.realiza_aporte != null
    ? Boolean(registro.realiza_aporte)
    : registro?.monto != null,
  monto: registro?.monto != null ? String(registro.monto) : '',
  tipo_moneda: registro?.tipo_moneda ?? '$',
  tasa_bcv_id: registro?.tasa_bcv_id ?? '',
  forma_pago_id: registro?.forma_pago_id ?? '',
  fecha_pago: (registro?.fecha_pago ?? '').toString().slice(0, 10),
  numero_comprobante: registro?.numero_comprobante ?? '',
});
